import { useEffect, useState } from 'react';
import { Save, Check, Lightbulb } from '@/components/icons';
import { ItemThumb } from '@/components/ui/ItemThumb';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Textarea } from '@/components/form/Textarea';
import { useToast } from '@/components/ui/useToast';
import { usePrompt } from '@/components/ui/usePrompt';
import { confirmPickList, getPickList, updatePickList, MAX_PICK_PHOTOS } from '@/api/pickList';
import { PickListPhotos } from './PickListPhotos';
import { apiError } from '@/lib/apiError';

interface Props {
  open: boolean;
  pickListId: number | null;
  onClose: () => void;
}

interface ItemState {
  id: number;
  itemName: string;
  unit: string;
  doQty: number;
  pickedQty: string;
  isChecked: boolean;
  discrepancyReason: string;
  currentStock: number;
  availableStock: number;
  imageUrl?: string | null;
  barcode?: string | null;
  locations: { code: string; qty: number }[];
}

export function PickListModal({ open, pickListId, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const ask = usePrompt();
  const { user } = useAuth();
  const [assignedTo, setAssignedTo] = useState('');
  const [supervisor, setSupervisor] = useState('');
  const [remarks, setRemarks] = useState('');
  const [items, setItems] = useState<ItemState[]>([]);
  const [confirmName, setConfirmName] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  const query = useQuery({
    queryKey: ['pick-list', pickListId],
    queryFn: () => getPickList(pickListId as number),
    enabled: open && !!pickListId,
  });
  const pl = query.data ?? null;
  const isReadOnly = pl?.status === 'Completed';

  useEffect(() => {
    if (!open) return;
    if (!pl) return;
    setIsDirty(false);
    setAssignedTo(pl.assignedTo);
    const supName = pl.supervisor ?? user?.fullName ?? '';
    setSupervisor(supName);
    setRemarks(pl.remarks ?? '');
    setConfirmName(supName);
    setItems(
      pl.items.map((it) => ({
        id: it.id,
        itemName: it.itemName,
        unit: it.unit,
        doQty: Number(it.doQty),
        pickedQty: it.pickedQty,
        isChecked: it.isChecked,
        discrepancyReason: it.discrepancyReason ?? '',
        currentStock: Number(it.currentStock ?? 0),
        availableStock: Number(it.availableStock ?? 0),
        imageUrl: it.imageUrl ?? null,
        barcode: it.barcode ?? null,
        locations: it.locations ?? [],
      })),
    );
  }, [open, pl]);

  function update(idx: number, patch: Partial<ItemState>) {
    setIsDirty(true);
    setItems((s) => s.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  /** Tick (or untick) every line at once. */
  function setAllChecked(isChecked: boolean) {
    setIsDirty(true);
    setItems((s) => s.map((it) => ({ ...it, isChecked })));
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      updatePickList(pickListId as number, {
        assignedTo: assignedTo.trim() || undefined,
        supervisor: supervisor.trim() || undefined,
        remarks: remarks.trim() || undefined,
        items: items.map((it) => ({
          pickListItemId: it.id,
          pickedQty: Number(it.pickedQty || 0),
          isChecked: it.isChecked,
          discrepancyReason: it.discrepancyReason.trim() || undefined,
        })),
      }),
    onSuccess: () => {
      setIsDirty(false);
      qc.invalidateQueries({ queryKey: ['pick-list'] });
      toast.push('Pick list updated', 'success');
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Save failed'), 'error'),
  });

  const confirmMutation = useMutation({
    mutationFn: () => confirmPickList(pickListId as number, confirmName.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pick-list'] });
      qc.invalidateQueries({ queryKey: ['packing'] });
      toast.pushStep('Pick list confirmed', {
        body: 'Packing record created — go pack the boxes.',
        href: '/sales/packing',
        nextLabel: 'Open Packing',
        tag: `pick-confirmed-${pickListId}`,
      });
      onClose();
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Confirm failed'), 'error'),
  });

  const allChecked = items.length > 0 && items.every((it) => it.isChecked);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={pl ? `Pick — ${pl.onlineOrder?.orderName ?? pl.pickNo}` : 'Pick List'}
      size="2xl"
      footer={
        isReadOnly ? (
          <Button variant="glass" onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button variant="glass" onClick={onClose}>Cancel</Button>
            <Button variant="info" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              <span className="inline-flex items-center gap-1"><Save size={14} /> Save Progress</span>
            </Button>
            <Button
              variant="success"
              onClick={async () => {
                if (!supervisor.trim()) {
                  toast.push('Supervisor name required', 'error');
                  return;
                }
                if (!allChecked) {
                  toast.push('Mark every line as checked first', 'error');
                  return;
                }
                const pickedOf = (it: ItemState) => Number(it.pickedQty || 0);
                if (!items.some((it) => pickedOf(it) > 0)) {
                  toast.push('No item picked — enter the picked qty for at least one item before confirming', 'error');
                  return;
                }
                const short = items.filter((it) => pickedOf(it) < it.doQty);
                if (short.length) {
                  const ok = await ask({
                    title: `${short.length} of ${items.length} item${items.length === 1 ? '' : 's'} short`,
                    message:
                      short
                        .slice(0, 8)
                        .map((it) => `• ${it.itemName} — picked ${pickedOf(it)} of ${it.doQty}`)
                        .join('\n') +
                      (short.length > 8 ? `\n• …and ${short.length - 8} more` : '') +
                      '\n\nOnly the picked qty goes to packing. Confirm anyway?',
                    input: false,
                    tone: 'danger',
                    confirmLabel: 'Confirm anyway',
                  });
                  if (ok === null) return;
                }
                // If the picker made unsaved edits, flush them first so the
                // confirmed pick list matches what's on screen. mutateAsync
                // throws on failure — abort the confirm and let the toast
                // from saveMutation.onError explain what went wrong.
                if (isDirty) {
                  try {
                    await saveMutation.mutateAsync();
                  } catch {
                    return;
                  }
                }
                setConfirmName(supervisor.trim());
                confirmMutation.mutate();
              }}
              disabled={confirmMutation.isPending || saveMutation.isPending}
              title={!allChecked ? 'Mark every line as checked first' : isDirty ? 'Save + confirm + create packing' : 'Confirm + create packing'}
            >
              {saveMutation.isPending || confirmMutation.isPending ? '…' : <><Check size={13} className="mr-1 inline-block align-[-2px]" /> Confirm</>}
            </Button>
          </>
        )
      }
    >
      {!pl ? (
        <div className="p-10 text-center text-sm text-[var(--text-muted)]">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 sm:grid-cols-3 sm:gap-3">
            {/* Online orders are tracked by their Shopify order number and buyer. */}
            <FormGroup label={pl.onlineOrder ? 'Order ID' : 'DO Ref'}>
              <Input value={(pl.onlineOrder ? pl.onlineOrder.orderName : pl.dispatchOrder?.doNo) ?? '—'} readOnly className="opacity-70" />
            </FormGroup>
            <FormGroup label="Customer">
              <Input value={pl.onlineOrder?.customerName ?? pl.dispatchOrder?.party?.name ?? '—'} readOnly className="opacity-70" />
            </FormGroup>
            <FormGroup label="Assigned To">
              <Input
                value={assignedTo}
                onChange={(e) => { setIsDirty(true); setAssignedTo(e.target.value); }}
                disabled={isReadOnly}
              />
            </FormGroup>
            <FormGroup label="Supervisor (preview)">
              <Input
                value={supervisor}
                onChange={(e) => { setIsDirty(true); setSupervisor(e.target.value); setConfirmName(e.target.value); }}
                disabled={isReadOnly}
              />
            </FormGroup>
            <FormGroup label="Remarks" className="col-span-2">
              <Textarea
                rows={2}
                value={remarks}
                onChange={(e) => { setIsDirty(true); setRemarks(e.target.value); }}
                disabled={isReadOnly}
              />
            </FormGroup>
            {/* Saves on its own (not with the form), so it stays open after the pick is completed.
                Regular orders only — online orders don't need segregation photos. */}
            {pl.status !== 'Cancelled' && !pl.onlineOrder && (
              <FormGroup label={`Segregated items photos (optional · up to ${MAX_PICK_PHOTOS})`} className="col-span-2">
                <PickListPhotos pickList={pl} active={open} compact />
              </FormGroup>
            )}
          </div>

          {(pl.dispatchOrder?.enquiry?.remarks || pl.dispatchOrder?.remarks) && (
            <div className="mt-3 space-y-1 rounded-md border border-info/30 bg-info/[0.06] px-3 py-2 text-xs">
              {pl.dispatchOrder?.enquiry?.remarks && (
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Enquiry Note</span>{' '}
                  <span className="text-[var(--text-secondary)]">{pl.dispatchOrder.enquiry.remarks}</span>
                </div>
              )}
              {pl.dispatchOrder?.remarks && (
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">DO Remark</span>{' '}
                  <span className="text-[var(--text-secondary)]">{pl.dispatchOrder.remarks}</span>
                </div>
              )}
            </div>
          )}

          {/* Mobile stacked cards */}
          <div className="mt-3 space-y-2 sm:hidden">
            {items.length > 0 && (
              <label className="flex items-center gap-2 px-1 text-[12px] font-bold text-[var(--text-secondary)]">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={(e) => setAllChecked(e.target.checked)}
                  disabled={isReadOnly}
                />
                Select all ({items.length})
              </label>
            )}
            {items.map((it, idx) => {
              const picked = Number(it.pickedQty || 0);
              const short = Math.max(0, it.doQty - picked);
              return (
                <div key={it.id} className="rounded-card border border-[var(--border-color)] p-2.5">
                  <div className="mb-1.5 flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={it.isChecked}
                      onChange={(e) => update(idx, { isChecked: e.target.checked })}
                      disabled={isReadOnly}
                      className="mt-0.5"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-[12px] font-bold text-[var(--text-primary)]">{it.itemName}</div>
                      {it.barcode && <div className="font-mono text-[11px] font-bold text-[var(--text-primary)]">{it.barcode}</div>}
                    </div>
                  </div>
                  {/* Quantities as four small tiles on one row */}
                  <div className="mb-1.5 grid grid-cols-4 gap-1 text-center">
                    {([
                      ['DO Qty', `${it.doQty} ${it.unit}`, 'text-[var(--text-primary)]'],
                      ['Short', String(short), short > 0 ? 'text-warning' : 'text-[var(--text-muted)]'],
                      ['Stock', `${it.currentStock} ${it.unit}`, it.currentStock <= 0 ? 'text-danger' : it.currentStock < it.doQty ? 'text-warning' : 'text-success'],
                      ['Available', `${it.availableStock} ${it.unit}`, it.availableStock <= 0 ? 'text-danger' : it.availableStock < it.doQty ? 'text-warning' : 'text-success'],
                    ] as const).map(([label, value, tone]) => (
                      <div key={label} className="rounded-md bg-[var(--bg-soft)] px-1 py-1">
                        <div className="text-[9px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
                        <div className={`whitespace-nowrap text-[12px] font-extrabold ${tone}`}>{value}</div>
                      </div>
                    ))}
                  </div>
                  {/* Picked qty and where to pick it from, side by side */}
                  <div className="mb-1.5 flex items-start gap-2">
                    <div className="w-24 shrink-0">
                      <div className="mb-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Picked</div>
                      <Input
                        type="number"
                        step="1"
                        value={it.pickedQty}
                        onChange={(e) => update(idx, { pickedQty: e.target.value })}
                        className="!w-24 text-right"
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="mb-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Location</div>
                      {it.locations.length === 0 ? (
                        <span className="text-[11px] italic text-[var(--text-muted)]">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {it.locations.map((loc) => (
                            <span
                              key={loc.code}
                              className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-bold ${
                                loc.code === 'Unracked'
                                  ? 'border-warning/40 bg-warning/10 text-warning'
                                  : 'border-accent/40 bg-accent/10 text-accent'
                              }`}
                              title={loc.code === 'Unracked' ? 'Received but not yet placed on a rack' : `Bin ${loc.code}`}
                            >
                              {loc.code}
                              <span className="text-[var(--text-muted)]">·</span>
                              <span>{loc.qty}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <Input
                    value={it.discrepancyReason}
                    onChange={(e) => update(idx, { discrepancyReason: e.target.value })}
                    placeholder={short > 0 ? 'Discrepancy — required when short' : 'Discrepancy (optional)'}
                    disabled={isReadOnly}
                  />
                </div>
              );
            })}
          </div>

          {/* Desktop table */}
          <div className="mt-4 hidden overflow-hidden rounded-card border border-[var(--border-color)] sm:block">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-accent/[0.05] text-[10px] uppercase tracking-wide text-[var(--text-muted)]">
                  <th className="px-2 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={(e) => setAllChecked(e.target.checked)}
                      disabled={isReadOnly || items.length === 0}
                      title={allChecked ? 'Untick all' : 'Select all'}
                      aria-label="Select all items"
                    />
                  </th>
                  <th className="whitespace-nowrap px-2 py-2 text-left">Item</th>
                  <th className="whitespace-nowrap px-2 py-2 text-left">Barcode</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right">DO Qty</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right">Picked</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right">Short</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right">Stock</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right">Available</th>
                  <th className="whitespace-nowrap px-2 py-2 text-left">Location</th>
                  <th className="whitespace-nowrap px-2 py-2 text-left">Discrepancy Reason</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => {
                  const picked = Number(it.pickedQty || 0);
                  const short = Math.max(0, it.doQty - picked);
                  return (
                    <tr key={it.id} className="border-b border-white/[0.04]">
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={it.isChecked}
                          onChange={(e) => update(idx, { isChecked: e.target.checked })}
                          disabled={isReadOnly}
                        />
                      </td>
                      <td className="px-2 py-2 font-bold">
                        <div className="flex items-center gap-2">
                          <ItemThumb src={it.imageUrl} />
                          <div className="whitespace-nowrap">{it.itemName}</div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 font-mono text-[11px] font-bold text-[var(--text-primary)]">{it.barcode || '—'}</td>
                      <td className="whitespace-nowrap px-2 py-2 text-right">{it.doQty} {it.unit}</td>
                      <td className="px-2 py-2 text-right">
                        <Input
                          type="number"
                          step="1"
                          value={it.pickedQty}
                          onChange={(e) => update(idx, { pickedQty: e.target.value })}
                          className="!w-20 text-right"
                          disabled={isReadOnly}
                        />
                      </td>
                      <td className={`px-2 py-2 text-right ${short > 0 ? 'font-extrabold text-warning' : 'text-[var(--text-muted)]'}`}>{short}</td>
                      <td className={`whitespace-nowrap px-2 py-2 text-right font-bold ${it.currentStock <= 0 ? 'text-danger' : it.currentStock < it.doQty ? 'text-warning' : 'text-success'}`}>
                        {it.currentStock} {it.unit}
                      </td>
                      <td className={`whitespace-nowrap px-2 py-2 text-right font-bold ${it.availableStock <= 0 ? 'text-danger' : it.availableStock < it.doQty ? 'text-warning' : 'text-success'}`}>
                        {it.availableStock} {it.unit}
                      </td>
                      <td className="px-2 py-2">
                        {it.locations.length === 0 ? (
                          <span className="text-[11px] italic text-[var(--text-muted)]">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {it.locations.map((loc) => (
                              <span
                                key={loc.code}
                                className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[11px] font-bold ${
                                  loc.code === 'Unracked'
                                    ? 'border-warning/40 bg-warning/10 text-warning'
                                    : 'border-accent/40 bg-accent/10 text-accent'
                                }`}
                                title={loc.code === 'Unracked' ? 'Received but not yet placed on a rack' : `Bin ${loc.code}`}
                              >
                                {loc.code}
                                <span className="text-[var(--text-muted)]">·</span>
                                <span>{loc.qty}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          value={it.discrepancyReason}
                          onChange={(e) => update(idx, { discrepancyReason: e.target.value })}
                          placeholder={short > 0 ? 'Required when short' : 'Optional'}
                          className="min-w-[11rem]"
                          disabled={isReadOnly}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!isReadOnly && (
            <div className="mt-3 rounded-md border border-info/30 bg-info/[0.05] p-3 text-xs">
              <FormGroup label="Supervisor Name (for confirm)" required>
                <Input
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder="Type your full name"
                />
              </FormGroup>
              <div className="mt-1 text-[11px] text-[var(--text-secondary)]">
                <Lightbulb size={13} className="mr-1 inline-block align-[-2px]" /> Once you confirm, a Packing record auto-opens for this DO. Reopen only by rejecting the packing.
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
